import React, { useEffect, useState } from "react";
import { Button, Card, Form, ListGroup, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import avatar from "../../assets/img/user-avatar.png";
import useManagePatient from "../../hooks/patientDashboard/useManagePatient";
import useProfileStore from "../../store/patient/profileStore";
import FundWalletModal from "../modals/fundWalletModal";
import WalletHistoryModal from "../modals/walletHistoryModal";
import BtnSpinner from "../common/btnSpinners";
import useWalletBalance from "../../hooks/patientDashboard/useWalletBalance";
import ProfileModal from "../modals/profileModal";
import { useDashboard } from "../../context/dashboardContext";
import { useLab } from "../../context/labContext";
import { useDrugs } from "../../context/manageDrugs";
import { useBills } from "../../context/paymentAndBillsContext";
import { parseAvatar } from "../../utils/chat/utils";
import { toast } from "sonner";

function getValidPatientId(patient) {
  const legacyId = patient?.legacy_patient_id;

  if (typeof legacyId === "string" && /^\d+$/.test(legacyId)) {
    return legacyId;
  }

  return patient?.id || "--";
}

const ProfileCard = () => {
  const { loading, handleImageUpload } = useManagePatient();
  const { handleFetAppointments, appointments } = useDashboard();
  const { handleFechLabRequestsAlt, requests } = useLab();
  const { handleFetchDrugs, drugs } = useDrugs();
  const { userBills, findPatientBillsAlt } = useBills();
  const { profile } = useProfileStore();

  const [showFundWalletModal, setShowFundWalletModal] = useState(false);
  const [showWalletHistory, setShowWalletHistory] = useState(false);
  const { balance: walletBalance, refetch: refetchWallet } = useWalletBalance();
  const [showProfilePicModal, setShowProfilePicModal] = useState(false);
  const [selectedImg, setSelectedImg] = useState(null);
  const [profileModal, setProfileModal] = useState(false);

  const navigate = useNavigate();
  const appointmentsList = Array.isArray(appointments) ? appointments : [];
  const requestsList = Array.isArray(requests) ? requests : [];
  const drugsList = Array.isArray(drugs) ? drugs : [];
  const userBillsList = Array.isArray(userBills) ? userBills : [];

  useEffect(() => {
    handleFetAppointments();
    handleFechLabRequestsAlt();
    handleFetchDrugs();
    findPatientBillsAlt();
  }, []);

  // Refresh the available balance after funding or after the history modal
  // closes, so a top-up / pay-from-wallet reflects immediately.
  useEffect(() => {
    refetchWallet();
  }, [showFundWalletModal, showWalletHistory, refetchWallet]);

  const handleSave = async () => {
    if (!selectedImg) {
      toast.error("Please select a profile picture first.");
      return;
    }

    try {
      await handleImageUpload(selectedImg);
      setShowProfilePicModal(false);
      setSelectedImg(null);
    } catch (error) {
      console.error("Error updating profile picture:", error);
    }
  };

  const upcomingAppointments = appointmentsList.filter(
    (appointment) =>
      appointment?.status !== "Cancelled" &&
      moment(appointment?.appointment_date).isAfter(moment())
  );

  const profileStats = [
    {
      label: "Upcoming visits",
      value: upcomingAppointments.length,
    },
    {
      label: "Open lab requests",
      value: requestsList.length,
    },
    {
      label: "Medications",
      value: drugsList.length,
    },
    {
      label: "Pending bills",
      value: userBillsList.length,
    },
  ];

  const detailItems = [
    {
      label: "Blood Group",
      value: profile?.blood_group || "--",
    },
    {
      label: "Genotype",
      value: profile?.genotype || "--",
    },
    {
      label: "Next of Kin",
      value: profile?.nextOfKin?.surname || "Not added",
    },
    {
      label: "Hospital ID",
      value: getValidPatientId(profile),
    },
  ];

  const fullName =
    `${profile?.surname || ""} ${profile?.other_names || ""}`.trim() || "Patient";

  return (
    <>
      <Card className="card-event profile-card-shell">
        <Link to="" onClick={() => setShowProfilePicModal(true)}>
          <img
            src={profile?.profile_pic ? parseAvatar(profile.profile_pic) : avatar}
            className="card-img-top profile-pic"
            alt={fullName}
          />
        </Link>

        <Card.Body>
          <div className="profile-card-header">
            <div className="profile-card-heading">
              <div className="profile-card-badge">Patient account</div>
              <h1 className="finance-value mt-3">
                {profile?.surname || "Your"} <span>{profile?.other_names || "Profile"}</span>
              </h1>
              <p>
                {[profile?.date_of_birth, profile?.gender, profile?.maritalStatus]
                  .filter(Boolean)
                  .join(" • ") || "Complete your profile to unlock a fuller care summary."}
              </p>
            </div>

            <Button
              variant=""
              className="btn-white"
              onClick={() => setShowProfilePicModal(true)}
            >
              Change Photo
            </Button>
          </div>

          <p className="text-secondary fs-sm mb-4">
            {profile?.address || "Add your contact address so your care team always has the right details."}
          </p>

          <div className="profile-balance-panel">
            <div className="d-flex align-items-center justify-content-between">
              <div className="divider divider-start fw-medium flex-grow-1">
                <span>Available balance</span>
              </div>
              <Link to="" onClick={() => setShowWalletHistory(true)}>
                View history
              </Link>
            </div>

            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <h2 className="finance-value mb-0">
                {(typeof walletBalance === "number"
                  ? walletBalance
                  : typeof profile?.depositBalance === "number"
                  ? profile.depositBalance
                  : 0
                ).toLocaleString("en-NG", {
                  style: "currency",
                  currency: "NGN",
                })}
              </h2>

              <Button className="btn-deda" onClick={() => setShowFundWalletModal(true)}>
                Fund Wallet
              </Button>
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between mb-3">
            <label className="mg-b-0 fw-medium text-secondary">Care summary</label>
            <Link to="" onClick={() => setProfileModal(true)}>
              Show details
            </Link>
          </div>

          <div className="profile-key-stats">
            {profileStats.map((item) => (
              <div className="profile-key-stat" key={item.label}>
                <small>{item.label}</small>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>

          <ListGroup className="profile-detail-list" variant="flush">
            {detailItems.map((item) => (
              <ListGroup.Item key={item.label} className="d-flex justify-content-between">
                <span className="fw-medium">{item.label}</span>
                <span className="ff-numerals">{item.value}</span>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card.Body>

        <Card.Footer>
          <div className="profile-card-actions w-100">
            <Button onClick={() => navigate("/appointment/schedule")}>
              Book Appointment
            </Button>
            <Button variant="" className="btn-white" onClick={() => setProfileModal(true)}>
              View Full Profile
            </Button>
          </div>
        </Card.Footer>
      </Card>

      <FundWalletModal setShow={setShowFundWalletModal} show={showFundWalletModal} />
      <WalletHistoryModal setShow={setShowWalletHistory} show={showWalletHistory} />
      <ProfileModal avatar={avatar} show={profileModal} setShow={setProfileModal} />
      <ProfilePicModal
        loading={loading}
        handleSave={handleSave}
        setSelectedImg={setSelectedImg}
        selectedImg={selectedImg}
        setShow={setShowProfilePicModal}
        show={showProfilePicModal}
      />
    </>
  );
};

const ProfilePicModal = ({
  loading,
  show,
  handleSave,
  setSelectedImg,
  setShow,
}) => {
  const handleClose = () => setShow(false);

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Change Profile Picture</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group>
            <Form.Label>Upload a new picture</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(event) => setSelectedImg(event.target.files[0])}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleSave}>
          {loading ? <BtnSpinner /> : "Save Changes"}
        </Button>
        <Button variant="" className="btn-white" onClick={handleClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProfileCard;
